import { z } from 'zod';
import { registry } from '../registry';
import { neurosensoryCardTitles } from './neurological';
export const painAssessmentCardTitle = neurosensoryCardTitles.painAssessment;
const painScaleOptions = [
    { value: '0', label: '0 — No Pain' },
    { value: '1', label: '1 — Very Mild' },
    { value: '2', label: '2 — Discomforting' },
    { value: '3', label: '3 — Tolerable' },
    { value: '4', label: '4 — Distressing' },
    { value: '5', label: '5 — Very Distressing' },
    { value: '6', label: '6 — Intense' },
    { value: '7', label: '7 — Very Intense' },
    { value: '8', label: '8 — Severe' },
    { value: '9', label: '9 — Very Severe' },
    { value: '10', label: '10 — Worst Pain Possible' },
];
export const basePainAssessmentSchema = z
    .object({
    hasPain: z
        .string()
        .optional()
        .register(registry, {
        label: 'Has the patient had any pain this week?',
        options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
            { value: 'unable-to-communicate', label: 'Unable to communicate' },
        ],
    }),
    primarySite: z.string().optional().register(registry, { label: 'Primary Site' }),
    receivedBy: z
        .string()
        .optional()
        .register(registry, {
        label: 'Received By',
        options: [
            { value: 'oral', label: 'Oral' },
            { value: 'iv', label: 'IV' },
            { value: 'sq-injection', label: 'SQ Injection' },
            { value: 'im', label: 'IM' },
            { value: 'topical', label: 'Topical' },
            { value: 'transdermal-patch', label: 'Transdermal Patch' },
            { value: 'epidural', label: 'Epidural' },
        ],
    }),
    interferesWithActivity: z
        .string()
        .optional()
        .register(registry, {
        label: 'Interferes with Activity',
        options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
        ],
    }),
    currentPainIntensity: z
        .string()
        .optional()
        .register(registry, { label: 'Current Pain Intensity', options: [...painScaleOptions] }),
    pastPainBest: z
        .string()
        .optional()
        .register(registry, { label: 'Past Pain Best / Least', options: [...painScaleOptions] }),
    pastPainWorst: z
        .string()
        .optional()
        .register(registry, { label: 'Past Pain Worst / Most', options: [...painScaleOptions] }),
    description: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Description',
        options: [
            { value: 'aching', label: 'Aching' },
            { value: 'burning', label: 'Burning' },
            { value: 'dull', label: 'Dull' },
            { value: 'sharp', label: 'Sharp' },
            { value: 'shooting', label: 'Shooting' },
            { value: 'tender', label: 'Tender' },
            { value: 'throbbing', label: 'Throbbing' },
        ],
    }),
    nonverbalPainCues: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Nonverbal Pain Cues',
        options: [
            { value: 'na', label: 'N/A' },
            { value: 'grimacing', label: 'Grimacing' },
            { value: 'guarding', label: 'Guarding' },
            { value: 'crying', label: 'Crying' },
            { value: 'bracing', label: 'Bracing' },
            { value: 'sitting-position', label: 'Sitting position' },
            { value: 'stiff', label: 'Stiff' },
        ],
    }),
    relievedBy: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Pain is Relieved / Mitigated By',
        options: [
            { value: 'weight-bearing', label: 'Change in weight-bearing' },
            { value: 'cold', label: 'Cold' },
            { value: 'distraction', label: 'Distraction' },
            { value: 'heat', label: 'Heat' },
            { value: 'massage', label: 'Massage' },
            { value: 'medication', label: 'Medication' },
            { value: 'meditation', label: 'Meditation' },
            { value: 'reposition', label: 'Reposition' },
            { value: 'rest', label: 'Rest' },
            { value: 'sleep', label: 'Sleep' },
            { value: 'visualization', label: 'Visualization' },
            { value: 'other', label: 'Other' },
        ],
    }),
    relievedByOther: z.string().optional().register(registry, { label: 'Other — specify' }),
    managementEffectiveness: z
        .string()
        .optional()
        .register(registry, {
        label: 'Current Pain Management Effectiveness',
        options: [
            { value: 'decline-mood', label: 'Decline in mood' },
            { value: 'decline-sleep', label: 'Decline in sleep pattern' },
            { value: 'decline-psychosocial', label: 'Decline in psychosocial function' },
            { value: 'decline-physical', label: 'Decline in physical function' },
            { value: 'improvement-mood', label: 'Improvement in mood' },
            { value: 'improvement-sleep', label: 'Improvement in sleep pattern' },
            { value: 'improvement-psychosocial', label: 'Improvement in psychosocial function' },
            { value: 'improvement-physical', label: 'Improvement in physical function' },
            { value: 'stabilization-mood', label: 'Stabilization in mood' },
            { value: 'stabilization-sleep', label: 'Stabilization in sleep pattern' },
            { value: 'stabilization-psychosocial', label: 'Stabilization in psychosocial function' },
            { value: 'stabilization-physical', label: 'Stabilization in physical function' },
        ],
    }),
    aberrantBehavior: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Potential Aberrant Behavior Noted (select all that apply)',
        options: [
            { value: 'na', label: 'N/A' },
            { value: 'appears-intoxicated', label: 'Appears intoxicated' },
            { value: 'other-physician-rx', label: 'Attempt to obtain RX from other physician' },
            { value: 'hoarding', label: 'Hoarding of prescriptions' },
            { value: 'dosage-increase', label: 'Increase to dosage without physician approval' },
            { value: 'unkempt-impaired', label: 'Increasingly unkempt / impaired' },
            { value: 'purposeful-sedation', label: 'Purposeful over sedation' },
            { value: 'lost-stolen-rx', label: 'Reports lost / stolen prescriptions' },
            { value: 'early-rx-renewal', label: 'Request frequent / early RX renewal' },
            { value: 'situational-stress', label: 'Use of pain meds for situational stress' },
            { value: 'md-notified', label: 'MD Notified of Aberrant Behavior' },
            { value: 'clinician-notified', label: 'Clinician Notified of Aberrant Behavior' },
        ],
    }),
    painFrequency: z
        .string()
        .optional()
        .register(registry, {
        label: "Current Frequency of Pain Interfering with Patient's Activity or Movement",
        options: [
            { value: 'no-pain', label: 'Patient has no pain' },
            { value: 'no-interference', label: 'Pain does not interfere with activity' },
            { value: 'less-than-daily', label: 'Less often than daily' },
            { value: 'daily-not-constant', label: 'Daily, but not constantly' },
            { value: 'constant', label: 'Patient is in constant pain' },
        ],
    }),
    comments: z.string().optional().register(registry, { label: 'Comments' }),
    nonverbalSymptoms: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Non-Verbal Symptoms of Pain Demonstrated',
        options: [
            { value: 'anger', label: 'Anger' },
            { value: 'crying', label: 'Crying' },
            { value: 'restless', label: 'Restless' },
            { value: 'diaphoresis', label: 'Diaphoresis' },
            { value: 'elevated-hr', label: 'Elevated heart rate' },
            { value: 'grimacing', label: 'Grimacing' },
            { value: 'guarding', label: 'Guarding' },
            { value: 'increased-respiration', label: 'Increased respiration' },
            { value: 'irritable', label: 'Irritable' },
            { value: 'moaning', label: 'Moaning' },
            { value: 'tense', label: 'Tense' },
        ],
    }),
    adlImpact: z.string().optional().register(registry, {
        label: "How does pain interfere or impact the patient's activities of daily living?",
    }),
    reliefMeasures: z.string().optional().register(registry, { label: 'Current pain relief measures / effectiveness' }),
})
    .register(registry, { label: neurosensoryCardTitles.painAssessment });
